import { useState, useMemo } from 'react';
import { createFileRoute, useNavigate, useParams } from '@tanstack/react-router';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../lib/api';
import { youtubeUrlToEmbed } from '../lib/youtube-embed';
import { sanitizeHtml } from '../lib/sanitize-html';
import type { CourseStepDto, StepContentBlockDto, UserCourseProgressDto } from '@prometeus/contracts';

type BlockWithQuestions = StepContentBlockDto & {
  questions?: {
    id: string;
    stem: string;
    type: string;
    explanation?: string;
    options: { id: string; text: string; order: number; isCorrect?: boolean }[];
  }[];
};

export const Route = createFileRoute('/certificacao/cursos/$courseId/etapa/$stepId')({
  component: EtapaPage,
});

function EtapaPage() {
  const { courseId, stepId } = useParams({ from: '/certificacao/cursos/$courseId/etapa/$stepId' });
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [responses, setResponses] = useState<Record<string, string>>({});

  const { data, isLoading } = useQuery({
    queryKey: ['certification-step', courseId, stepId],
    queryFn: async (): Promise<{ step: CourseStepDto; blocks: BlockWithQuestions[] }> => {
      const response = await api.get(`/certification/courses/${courseId}/steps/${stepId}`);
      return response.data?.data ?? response.data;
    },
    enabled: !!courseId && !!stepId,
  });

  const { data: progress } = useQuery<UserCourseProgressDto | null>({
    queryKey: ['certification-progress', courseId],
    queryFn: async () => {
      try {
        const response = await api.get(`/certification/courses/${courseId}/progress`);
        return response.data?.data ?? response.data;
      } catch {
        return null;
      }
    },
    enabled: !!courseId,
  });

  const isStepCompleted = Boolean(progress?.completedStepIds?.includes(stepId));

  const completeMutation = useMutation({
    mutationFn: async () => {
      const response = await api.post(
        `/certification/courses/${courseId}/steps/${stepId}/complete`,
        { responses }
      );
      return response.data?.data ?? response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['certification-progress', courseId] });
      queryClient.invalidateQueries({ queryKey: ['certification-course', courseId] });
      const steps = (queryClient.getQueryData(['certification-course', courseId]) as any)?.steps ?? [];
      const currentIndex = steps.findIndex((s: { id: string }) => s.id === stepId);
      const nextStep = steps[currentIndex + 1];
      if (nextStep) {
        navigate({
          to: '/certificacao/cursos/$courseId/etapa/$stepId',
          params: { courseId: courseId!, stepId: nextStep.id },
        });
      } else {
        navigate({ to: '/certificacao/cursos/$courseId', params: { courseId: courseId! } });
      }
    },
  });

  const step = data?.step;
  const blocks = data?.blocks ?? [];
  const quizBlocks = useMemo(
    () => blocks.filter((b) => b.type === 'quiz') as BlockWithQuestions[],
    [blocks]
  );
  const allQuestions = useMemo(
    () => quizBlocks.flatMap((b) => b.questions ?? []),
    [quizBlocks]
  );
  const requiredQuestions = allQuestions.filter((q) => q && q.id);
  const answeredCount = requiredQuestions.filter((q) => responses[q.id]).length;
  const canComplete = requiredQuestions.length === 0 || answeredCount === requiredQuestions.length;

  if (isLoading || !step) {
    return (
      <div className="rounded-[10px] bg-white dark:bg-gray-800 p-8 animate-pulse" style={{ boxShadow: '0 0 15px rgba(0,0,0,0.05)' }}>
        <div className="h-6 w-3/4 bg-[#9BA8AB]/30 dark:bg-gray-700 rounded-xl mb-4" />
        <div className="h-4 w-full bg-[#9BA8AB]/20 dark:bg-gray-700 rounded-xl mb-2" />
        <div className="h-4 w-full bg-[#9BA8AB]/20 dark:bg-gray-700 rounded-xl" />
      </div>
    );
  }

  return (
    <div
      className="rounded-[10px] bg-white dark:bg-gray-800 overflow-hidden"
      style={{ fontFamily: "'Poppins', sans-serif", boxShadow: '0 0 15px rgba(0,0,0,0.05)' }}
    >
      <div className="p-6 border-b border-[#9BA8AB]/30 dark:border-gray-600">
        <h2 className="text-xl font-semibold text-[#06141B] dark:text-white">{step.title}</h2>
      </div>
      <div className="p-6 space-y-8">
        {blocks.map((block) => (
          <BlockRenderer
            key={block.id}
            block={block}
            responses={responses}
            onResponse={(questionId, optionId) =>
              setResponses((prev) => ({ ...prev, [questionId]: optionId }))
            }
          />
        ))}
      </div>
      <div className="p-6 border-t border-[#9BA8AB]/30 dark:border-gray-600 flex justify-between items-center">
        <p className="text-sm text-[#4A5C6A] dark:text-gray-400">
          {isStepCompleted
            ? 'Esta etapa já foi concluída.'
            : requiredQuestions.length > 0
              ? `${answeredCount} de ${requiredQuestions.length} questões respondidas`
              : 'Marque a etapa como concluída para continuar.'}
        </p>
        {isStepCompleted ? (
          <span className="px-4 py-2 text-sm rounded-[10px] bg-[#9BA8AB]/20 dark:bg-gray-700/50 text-[#06141B] dark:text-gray-300 font-medium cursor-default">
            Já concluída
          </span>
        ) : (
          <button
            type="button"
            onClick={() => completeMutation.mutate()}
            disabled={!canComplete || completeMutation.isPending}
            className="px-4 py-2 text-sm rounded-[10px] bg-[#06141B] text-white font-medium hover:bg-[#11212D] disabled:opacity-50 disabled:cursor-not-allowed transition-colors focus:outline-none focus:ring-0"
          >
            {completeMutation.isPending ? 'Salvando...' : 'Concluir etapa'}
          </button>
        )}
      </div>
    </div>
  );
}

function BlockRenderer({
  block,
  responses,
  onResponse,
}: {
  block: BlockWithQuestions;
  responses: Record<string, string>;
  onResponse: (questionId: string, optionId: string) => void;
}) {
  if (block.type === 'text') {
    const content = block.content as { body?: string; html?: string };
    const html = sanitizeHtml(content?.html ?? content?.body ?? '');
    if (html) {
      return (
        <div
          className="rounded-[10px] p-4 bg-white dark:bg-gray-800 prose dark:prose-invert max-w-none text-gray-700 dark:text-gray-300 [&_p]:text-[12px] [&_p]:text-justify [&_li]:text-[12px] [&_span]:text-[12px]"
          style={{ boxShadow: '0 0 15px rgba(0,0,0,0.05)' }}
          dangerouslySetInnerHTML={{ __html: html }}
        />
      );
    }
    return (
      <div
        className="rounded-[10px] p-4 bg-white dark:bg-gray-800 prose dark:prose-invert max-w-none text-gray-700 dark:text-gray-300 [&_p]:text-[12px] [&_p]:text-justify"
        style={{ boxShadow: '0 0 15px rgba(0,0,0,0.05)' }}
      >
        <p>{JSON.stringify(block.content)}</p>
      </div>
    );
  }

  if (block.type === 'video') {
    const content = block.content as { url?: string; embedUrl?: string };
    const rawUrl = content?.embedUrl ?? content?.url ?? '';
    const embedUrl = rawUrl ? youtubeUrlToEmbed(rawUrl) : '';
    if (!embedUrl) {
      return (
        <div className="rounded-[10px] p-4 bg-white dark:bg-gray-800 text-gray-500 dark:text-gray-400" style={{ boxShadow: '0 0 15px rgba(0,0,0,0.05)' }}>
          Vídeo não disponível.
        </div>
      );
    }
    return (
      <div className="rounded-[10px] p-4 bg-white dark:bg-gray-800 overflow-hidden" style={{ boxShadow: '0 0 15px rgba(0,0,0,0.05)' }}>
        <div className="aspect-video rounded-[10px] overflow-hidden bg-black">
          <iframe
            src={embedUrl}
            title="Vídeo"
            className="w-full h-full"
            allowFullScreen
          />
        </div>
      </div>
    );
  }

  if (block.type === 'audio') {
    const content = block.content as { url?: string };
    const url = content?.url ?? '';
    if (!url) {
      return (
        <div className="rounded-[10px] p-4 bg-white dark:bg-gray-800 text-gray-500 dark:text-gray-400 text-center" style={{ boxShadow: '0 0 15px rgba(0,0,0,0.05)' }}>
          Áudio não disponível.
        </div>
      );
    }
    return (
      <div
        className="rounded-[10px] p-4 bg-white dark:bg-gray-800 flex flex-col sm:flex-row items-center gap-4"
        style={{ boxShadow: '0 0 15px rgba(0,0,0,0.05)' }}
      >
        <div
          className="flex-shrink-0 w-14 h-14 rounded-[10px] bg-[#06141B]/10 dark:bg-gray-700/50 flex items-center justify-center"
          aria-hidden
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            fill="currentColor"
            className="w-7 h-7 text-[#06141B] dark:text-gray-300"
          >
            <path d="M12 3v10.55c-.59-.34-1.27-.55-2-.55-2.21 0-4 1.79-4 4s1.79 4 4 4 4-1.79 4-4V7h4V3h-6z" />
          </svg>
        </div>
        <div className="flex-1 w-full min-w-0">
          <p className="text-sm font-medium text-[#06141B] dark:text-gray-200 mb-2">Áudio</p>
          <audio controls className="w-full max-w-md h-10" src={url}>
            Seu navegador não suporta áudio.
          </audio>
        </div>
      </div>
    );
  }

  if (block.type === 'image') {
    const content = block.content as { url?: string; alt?: string };
    const url = content?.url ?? '';
    const alt = content?.alt ?? 'Imagem';
    if (!url) {
      return (
        <div className="rounded-[10px] p-4 bg-white dark:bg-gray-800 text-gray-500 dark:text-gray-400" style={{ boxShadow: '0 0 15px rgba(0,0,0,0.05)' }}>
          Imagem não disponível.
        </div>
      );
    }
    return (
      <div className="rounded-[10px] p-4 bg-white dark:bg-gray-800 flex justify-center" style={{ boxShadow: '0 0 15px rgba(0,0,0,0.05)' }}>
        <img src={url} alt={alt} className="w-full h-auto max-w-2xl rounded-[10px] object-contain" />
      </div>
    );
  }

  if (block.type === 'quiz' && block.questions) {
    return (
      <div className="rounded-[10px] p-4 bg-white dark:bg-gray-800 space-y-6" style={{ boxShadow: '0 0 15px rgba(0,0,0,0.05)' }}>
        <h3 className="font-semibold text-[#06141B] dark:text-white" style={{ fontSize: 16 }}>Questões</h3>
        {block.questions
          .slice()
          .sort((a, b) => (a as any).order - (b as any).order)
          .map((q) => (
            <div
              key={q.id}
              className="rounded-[10px] p-4 bg-gray-50/50 dark:bg-gray-700/30"
            >
              <p className="font-semibold text-[#06141B] dark:text-white mb-3" style={{ fontSize: 16 }}>{q.stem}</p>
              <div className="space-y-2">
                {(q.options ?? [])
                  .slice()
                  .sort((a, b) => a.order - b.order)
                  .map((opt) => (
                    <label
                      key={opt.id}
                      className="flex items-center gap-2 cursor-pointer p-2 rounded-xl hover:bg-[#9BA8AB]/10 dark:hover:bg-gray-700/50 transition-colors"
                    >
                      <input
                        type="radio"
                        name={q.id}
                        value={opt.id}
                        checked={responses[q.id] === opt.id}
                        onChange={() => onResponse(q.id, opt.id)}
                        className="rounded-full border-[#4A5C6A]/50 focus:outline-none focus:ring-0"
                      />
                      <span className="text-[#06141B] dark:text-gray-300" style={{ fontSize: 12 }}>{opt.text}</span>
                    </label>
                  ))}
              </div>
            </div>
          ))}
      </div>
    );
  }

  return null;
}
